import React, { useState, useEffect, useRef } from 'react';
import { Avatar } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { useWebSocket } from '../context/WebSocketContext';
import { useUser } from '../context/UserContext';
import featuredWorks from '../data/featuredWorks.json';

// 导入 Cormorant 字体
const GlobalStyle = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant:wght@500&display=swap');
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: ${({ theme }) => theme.background || '#ffffff'};
  position: relative;
  overflow: hidden;
  padding: 0;
`;

const LogoWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;
  margin-bottom: 30px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px 24px; /* 明确设置左右padding为24px */
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-top: 20px;
    width: 100%;
    box-sizing: border-box;
  }
`;

const LogoContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  position: relative;
  width: auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    margin-bottom: 16px;
    max-width: 100%; /* 确保不会超出容器 */
    overflow: visible; /* 允许内容可见 */
    box-sizing: border-box; /* 确保padding包含在宽度中 */
  }
`;

const Logo = styled.img`
  width: 500px;
  height: 200px;
  object-fit: contain;
  display: block;
  opacity: 1;
  transition: opacity 0.3s ease;
  margin-bottom: 20px;
  filter: none;
  box-shadow: none;
  text-shadow: none;
  -webkit-filter: none;
  -webkit-box-shadow: none;

  @media (max-width: 1440px) {
    width: 450px;
    height: 180px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 1024px) {
    width: 400px;
    height: 160px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 768px) {
    width: 360px;
    height: 120px;
    margin-bottom: 15px;
    max-width: 100%; /* 确保不超出容器宽度 */
  }
  
  @media (max-width: 480px) {
    width: 300px;
    height: 100px;
    margin-bottom: 15px;
    max-width: 100%; /* 确保不超出容器宽度 */
  }
  
  @media (max-width: 375px) {
    width: 240px;
    height: 80px;
    margin-bottom: 15px;
    max-width: 100%; /* 确保不超出容器宽度 */
  }
`;

const PageContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  margin-top: ${({ $hasBattle }) => $hasBattle ? '300px' : '0'};
  padding: 0 24px;
  box-sizing: border-box;
  transition: margin 0.15s ease-out;
`;

const ScrollContainer = styled.div`
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const EndMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  margin-top: 20px;
`;

const BattleTitle = styled.div`
  font-family: 'HanaMinA';
  font-size: 78px;
  line-height: 1;
  color: ${({ theme }) => theme.text};
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 60px;
  }
  
  @media (max-width: 480px) {
    font-size: 48px;
  }
`;

const LogoTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 20px;
`;

const PromptBattleTitle = styled.div`
  font-size: 78px;
  font-weight: bold;
  line-height: 1;
  color: #888888;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 60px;
  }
  
  @media (max-width: 480px) {
    font-size: 48px;
  }
`;

const SignatureText = styled.div`
  font-size: 48px;
  font-family: cursive;
  color: #CCCCCC;
  opacity: 0.8;
  position: absolute;
  top: 10px;
  left: 10px;
  transform: rotate(-5deg);
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const HistoryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #ff4d4f;
  color: #ffffff;
  border: none;
  border-radius: 200px;
  padding: 7px 24px;
  width: 238px;
  height: 72px;
  font-family: 'HanaMinA';
  font-weight: 500;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: visible;
  position: absolute;
  top: 128px;
  right: 24px;
  transform: none;
  
  svg {
    width: 64px;
    height: 64px;
    margin-left: 8px;
    
    path {
      transform-origin: left;
      transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      stroke-width: 2;
    }

    @media (max-width: 768px) {
      width: 48px;
      height: 48px;
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
    }
  }
  
  &:hover {
    background: #f44d4f;
    
    svg path {
      d: path("M0 10h40M40 10c-2 0-8-4-12-8M40 10c-2 0-8 4-12 8");
    }
  }
  
  &:active {
    background: #e64545;
  }
  
  @media (max-width: 1440px) {
    right: 24px;
  }
  
  @media (max-width: 1024px) {
    right: 24px;
  }
  
  @media (max-width: 768px) {
    position: relative;
    align-self: flex-start;
    width: 200px;
    height: 60px;
    font-size: 28px;
    padding: 7px 20px;
    margin-top: 0;
    top: auto;
    right: auto;
    transform: none;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
  
  }
`;

const ItemWrapper = styled.div`
  margin-bottom: 24px;
  cursor: pointer;
  transition: transform 0.2s;
  background-color: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  padding: 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    background-color: ${({ theme }) => theme.isDarkMode ? '#333' : 'white'};
    opacity: 0;
    z-index: -1;
    transition: opacity 120ms ease-out;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-3px);
    
    &::after {
      opacity: ${({ theme }) => theme.isDarkMode ? 0.5 : 0.7};
    }
  }
`;

const ItemImage = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 0;
  margin-bottom: 0;
  background: ${({ theme }) => theme.background};
  cursor: zoom-in;
  transition: all 0.3s ease;
  display: block;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
`;

const ItemTitle = styled.div`
  font-size: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.isDarkMode ? 'white' : 'black'};
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 1px;
  height: 24px;
`;

const AuthorAvatar = styled(Avatar)`
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  line-height: 24px !important;
  border-radius: 50% !important;
  font-size: 12px !important;
  overflow: hidden !important;
  
  .ant-avatar-string {
    line-height: 24px !important;
    font-size: 12px !important;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const AuthorName = styled.div`
  width: auto;
  min-width: 28px;
  height: 24px;
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  letter-spacing: 0%;
  text-align: center;
  font-weight: 500;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#111'};
  white-space: nowrap;

  /* 使用 :lang 选择器为不同语言设置不同字体 */
  &:lang(en) {
    font-family: 'SimSun', serif;
  }

  &:lang(zh), &:not(:lang(en)) {
    font-family: 'HanaMinA';
  }
`;

const MasonryGrid = styled(Masonry)`
  display: flex;
  gap: 24px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  
  .masonry-grid_column {
    flex: 1 1 0;
    min-width: 0;
    background-clip: padding-box;
  }
`;

const BattleBanner = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.isDarkMode ? 'white' : '#000'};
  z-index: 8;
  opacity: 1;
  transition: all 0.15s ease-out;
  padding: 20px 0;
  backdrop-filter: none;
  box-shadow: none;
`;

const BannerContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.15s ease-out;
  
  @media (max-width: 1440px) {
    max-width: 100%;
  }
`;

const BannerTitle = styled.h1`
  font-family: HanaMinA;
  font-size: 64px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: 0%;
  width: 704px;
  height: 88px;
  margin: 0 0 10px 0;
  position: relative;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    font-size: 32px;
    width: 100%;
    height: auto;
  }
`;

const BannerThemeLabel = styled.span`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: block;
  margin-bottom: 5px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;
  transition: all 0.15s ease-out;
`;

const BannerDescription = styled.div`
  font-size: 16px;
  font-weight: normal;
  opacity: 0.8;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  color: ${({ theme, $isFixed }) =>
    $isFixed
      ? theme.isDarkMode ? 'white' : 'black'
      : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  position: relative;
  z-index: 1;
  transition: all 0.15s ease-out;
  
  .description-label {
    margin-bottom: 4px;
    font-weight: normal;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
  
  .description-content-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const BannerAppendix = styled.a`
  font-size: 16px;
  font-weight: normal;
  color: var(--brand, #FF4B3F);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 1;
  &:hover {
    text-decoration: underline;
  }
  
  .download-icon {
    display: inline-flex;
    align-items: center;
  }
  
  .preview-img {
    width: 20px;
    height: 20px;
    object-fit: cover;
    border-radius: 2px;
    margin-right: 4px;
  }
`;

const BannerStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BannerPhase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  transition: all 0.15s ease-out;
  
  span {
    font-size: 16px;
    font-weight: normal;
    opacity: 0.8;
    color: ${({ theme, $isFixed }) =>
      $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
    };
    text-shadow: ${({ theme, $isFixed }) =>
      $isFixed
        ? 'none'
        : theme.isDarkMode ? 'none' : 'none'
    };
  }
  
  @media (max-width: 768px) {
    span {
      font-size: 14px;
    }
  }
`;

const BannerTimer = styled.div`
  font-family: 'Cormorant', 'Times New Roman', serif;
  font-weight: 500;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'tnum' on, 'lnum' on;
  color: ${({ $isPaused, theme, $isFixed }) =>
    $isPaused
      ? '#FF4B3F'
      : $isFixed
        ? theme.isDarkMode ? 'white' : 'black'
        : theme.isDarkMode ? 'white' : 'black'
  };
  text-shadow: ${({ theme, $isFixed }) =>
    $isFixed
      ? 'none'
      : theme.isDarkMode ? 'none' : 'none'
  };
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.15s ease-out;
  
  @media (max-width: 768px) {
    font-size: 36px;
    font-family: 'Cormorant', 'Times New Roman', serif !important;
    font-variant-numeric: lining-nums tabular-nums !important;
    font-feature-settings: 'tnum' on, 'lnum' on !important;
  }
`;

const PauseText = styled.span`
  font-size: 16px;
  color: #FF4B3F;
  line-height: 100%;
  margin-left: 10px;
  font-weight: normal;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-left: 8px;
  }
`;

const BannerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #ff4d4f;
  color: #ffffff;
  border: none;
  border-radius: 200px;
  padding: 7px 24px;
  width: 238px;
  height: 72px;
  font-family: 'HanaMinA';
  font-weight: 500;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: visible;
  position: relative;
  margin-top: 0;
  transform: none;
  
  svg {
    width: 64px;
    height: 64px;
    margin-left: 8px;
    
    path {
      transform-origin: left;
      transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      stroke-width: 2;
    }

    @media (max-width: 768px) {
      width: 48px;
      height: 48px;
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
    }
  }
  
  &:hover {
    background: #f44d4f;
    
    svg path {
      d: path("M0 10h40M40 10c-2 0-8-4-12-8M40 10c-2 0-8 4-12 8");
    }
  }
  
  &:active {
    background: #e64545;
  }
  
  @media (max-width: 1440px) {
    font-size: 28px;
  }
  
  @media (max-width: 768px) {
    width: 200px;
    height: 60px;
    padding: 7px 20px;
    font-size: 24px;
  }
  
  @media (max-width: 375px) {
    width: 180px;
    height: 50px;
    padding: 5px 16px;
    font-size: 20px;
  }
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: default;
`;

const FullscreenImage = styled.img`
  max-width: 95%;
  max-height: 95vh;
  object-fit: contain;
  cursor: default;
  background: ${({ theme }) => theme.background};
  transform-origin: center center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
  }
`;

// Shuffle the fixed editorial selection once per homepage visit.
const shuffleWorks = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const ArtworkButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  margin: 0 0 16px;
  border: 0;
  background: transparent;
  cursor: zoom-in;
  &:focus-visible { outline: 3px solid #ff4d4f; outline-offset: 4px; }
`;

const Home = () => {
  const { isDarkMode, theme } = useTheme();
  const navigate = useNavigate();
  const [winners] = useState(() => shuffleWorks(featuredWorks));
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const activeArtworkButton = useRef(null);

  // WebSocket相关状态
  const {
    fightInfo: wsBattleData,
    fightStatus: wsBattleStatus,
    isConnected,
    lastSecond: wsCountdown,
    formatCountdown,
    isStop
  } = useWebSocket();

  // 瀑布流的断点设置
  const breakpointColumns = {
    default: 4,    // 默认4列
    1000: 3,       // 屏幕宽度 <= 1000px 时为3列
    700: 2,        // 屏幕宽度 <= 700px 时为2列
    500: 2         // 屏幕宽度 <= 500px 时保持2列
  };

  const handleImageClick = (item, event) => {
    activeArtworkButton.current = event.currentTarget;
    setFullscreenImage(item);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
    activeArtworkButton.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!fullscreenImage) return undefined;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFullscreenImage(null);
        activeArtworkButton.current?.focus({ preventScroll: true });
      }
      // The image dialog has one interactive control: keep focus on Close.
      if (event.key === 'Tab') event.preventDefault();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullscreenImage]);

  const goToHistory = () => {
    navigate('/history');
  };

  // 获取当前阶段文本
  const getStageText = () => {
    if (!wsBattleStatus || wsBattleStatus === 0 || wsBattleStatus === 40) {
      return '无对战';
    }
    switch (wsBattleStatus) {
      case 10:
        return '创作阶段';
      case 20:
        return '上传阶段';
      case 30:
        return '投票阶段';
      default:
        return '无对战';
    }
  };

  // 判断是否有进行中的对战
  const hasBattle = wsBattleStatus > 0 && wsBattleStatus < 40;
  
  // 获取用户登录状态
  const { user } = useUser();
  const isLoggedIn = !!user;

  return (
    <PageWrapper>
      <GlobalStyle />
      {fullscreenImage && (
        <FullscreenOverlay role="dialog" aria-modal="true" aria-label={fullscreenImage.title}
          onClick={(event) => { if (event.target === event.currentTarget) handleCloseFullscreen(); }}>
          <FullscreenImage
            src={fullscreenImage.large}
            alt={fullscreenImage.title + " — " + fullscreenImage.author}
            theme={theme}
          />
          <CloseButton autoFocus aria-label="关闭大图" onClick={handleCloseFullscreen}>
            <CloseOutlined />
          </CloseButton>
        </FullscreenOverlay>
      )}
      
      {hasBattle && isLoggedIn ? (
        <BattleBanner className="battle-banner">
          <BannerContainer>
            <BannerThemeLabel>主题</BannerThemeLabel>
            <BannerTitle>
              {wsBattleData?.fightNameCn ||
               (wsBattleData?.fightName && wsBattleData.fightName.includes('|') ?
                wsBattleData.fightName.split('|')[0].trim() :
                wsBattleData?.fightName) ||
               '当前未设置对战主题'}
            </BannerTitle>
            {(wsBattleData?.fightDesc || wsBattleData?.appendix) && (
              <BannerDescription>
                {wsBattleData?.fightDesc && <span className="description-label">描述</span>}
                <div className="description-content-row">
                  {wsBattleData?.fightDesc && <span>{wsBattleData?.fightDesc}</span>}
                  {wsBattleData?.appendix && (
                    <BannerAppendix
                      href={wsBattleData.appendix.startsWith('http') ? wsBattleData.appendix : `https://static.battleverse.cn${wsBattleData.appendix}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="preview-img"
                        src={wsBattleData.appendix.startsWith('http') ? wsBattleData.appendix : `https://static.battleverse.cn${wsBattleData.appendix}`}
                        alt="预览"
                      />
                      下载参考图
                      <div className="download-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M8 2v8M8 10l-3-3M8 10l3-3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </BannerAppendix>
                  )}
                </div>
              </BannerDescription>
            )}
            <BannerStatus>
              <BannerPhase>
                <span>{getStageText()}</span>
                <BannerTimer $isPaused={isStop === 1}>
                  {formatCountdown(wsCountdown)}
                  {isStop === 1 && <PauseText>（已暂停）</PauseText>}
                </BannerTimer>
              </BannerPhase>
              <BannerButton onClick={() => navigate('/battle')}>
                立即参与
                <svg width="64" height="64" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 10h30M30 10c-2 0-8-4-12-8M30 10c-2 0-8 4-12 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </BannerButton>
            </BannerStatus>
          </BannerContainer>
        </BattleBanner>
      ) :
        <LogoWrapper>
          <LogoContent>
            <Logo
              src={isDarkMode ? "/Group 2082894601_white.svg" : "/Group 2082894601.svg"}
              alt="Prompt Battle"
            />
          </LogoContent>
          {isLoggedIn && (
            <HistoryButton onClick={goToHistory}>
              <span>历史对战</span>
              <svg width="64" height="64" viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 10h30M30 10c-2 0-8-4-12-8M30 10c-2 0-8 4-12 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </HistoryButton>
          )}
        </LogoWrapper>
      }
      
      <PageContainer $hasBattle={hasBattle && isLoggedIn}>
        <ScrollContainer>
          <MasonryGrid
            breakpointCols={breakpointColumns}
            className="masonry-grid"
            columnClassName="masonry-grid_column"
            aria-label="精选获奖作品"
          >
            {winners.map((item, index) => (
              <ItemWrapper key={item.id} data-featured-id={item.id}>
                <ArtworkButton aria-label={'查看作品：' + item.title}
                  onClick={(event) => handleImageClick(item, event)}>
                  <ItemImage
                    src={item.src}
                    srcSet={item.srcSet}
                    sizes="(max-width: 700px) calc((100vw - 72px) / 2), (max-width: 1000px) calc((100vw - 96px) / 3), calc((100vw - 120px) / 4)"
                    alt={item.title + ' — ' + item.author}
                    width={item.width}
                    height={item.height}
                    loading={index < 4 ? 'eager' : 'lazy'}
                    decoding="async"
                    style={{ height: 'auto', aspectRatio: item.width + ' / ' + item.height, objectFit: 'contain' }}
                  />
                </ArtworkButton>
                <ItemTitle style={{ overflowWrap: 'anywhere' }}>{item.title}</ItemTitle>
                <AuthorInfo style={{ gap: '8px', height: 'auto', alignItems: 'flex-start' }}>
                  <AuthorAvatar aria-hidden="true" style={{ background: isDarkMode ? '#3a3a3a' : '#eee', color: isDarkMode ? '#fff' : '#444' }}>
                    {Array.from(item.author)[0]}
                  </AuthorAvatar>
                  <AuthorName style={{ whiteSpace: 'normal', height: 'auto', textAlign: 'left', overflowWrap: 'anywhere', minWidth: 0 }}>
                    {item.author}
                  </AuthorName>
                </AuthorInfo>
              </ItemWrapper>
            ))}
          </MasonryGrid>
          <EndMessage theme={theme}>已看完 100 张精选获奖作品</EndMessage>
        </ScrollContainer>
      </PageContainer>
    </PageWrapper>
  );
};

export default Home;
